// /out-licensor-deck — Public, shareable corporate deck for OUT-LICENSORS
// (biotechs and asset holders with assets to license out). Designed
// to be sent as a follow-up link to BD conversations. NO password
// gate — fully public. NO client name leakage. Pitches the simple
// ForesightRx value prop to biotechs: AI-powered MoA-driven outreach,
// non-exclusive, no retainer, no upfront, success-fee only, and
// SPEED. We do NOT sell our pharma-side scoring/committee process
// to biotechs — that's a separate retained service for in-licensors.
import React from 'react';
import { Zap, Mail, DollarSign, Network, Lock, Clock, Sparkles, Handshake, Globe2, MapPin } from 'lucide-react';
import { DeckShell } from '@/components/deck/DeckShell';

const SLIDES = [
  // ─── 1 · Title ──────────────────────────────────────
  {
    kind: 'cover',
    eyebrow: 'ForesightRx · For Out-Licensors',
    title: 'Get your asset to the right pharma. Fast.',
    sub: 'AI-driven outreach to the exact pharma buyers whose pipeline your asset fits — followed all the way through to a signed deal. Non-exclusive. No retainer. No upfront. We only get paid when you do.',
    foot: 'A short, honest brief · 8 slides',
  },

  // ─── 2 · The Problem ───────────────────────────────
  {
    kind: 'two-col',
    eyebrow: 'The Problem',
    title: 'Out-licensing today is slow, scattered, and expensive.',
    leftTitle: 'What most biotechs are stuck doing',
    leftBullets: [
      'Email-blasting 200 generic BD inboxes hoping one opens.',
      'Paying retainers of $15k–$40k/month for 6–12 months with no guarantee.',
      'Locking exclusive mandates that kill optionality and slow you down.',
      'Waiting weeks for "let me circulate internally" responses.',
    ],
    rightTitle: 'What actually moves a deal',
    rightBullets: [
      'Reaching only the pharmas whose pipeline genuinely needs your asset.',
      'A specific BD owner to email, not a generic alias.',
      'A crisp emailer that tells them in 30 seconds why this fits THEM.',
      'A partner who stays with you from intro to signing.',
    ],
    closer: 'We built ForesightRx to compress that loop from months to days.',
  },

  // ─── 3 · The Concept ───────────────────────────────
  {
    kind: 'process',
    eyebrow: 'What we do',
    title: 'AI-mapped outreach. End-to-end deal partnership.',
    sub: 'Our proprietary algorithm reads your asset across mechanism, disease, therapeutic area, modality, and stage — then matches it against the live pipelines, gaps, and stated BD priorities of every active in-licensing pharma globally. From the first email to the signed deal, we stay in the room with you.',
    steps: [
      { n: '1', title: 'Asset intake',          desc: 'Share your non-confidential deck. 15 minutes. Tell us which pharmas you\u2019re already in conversation with, your geographic preferences, and any other constraints. No NDA needed to establish initial fit.' },
      { n: '2', title: 'AI buyer mapping',      desc: 'Our algorithm matches your asset across MoA, disease, therapeutic area, modality, and stage — against every active pharma in-licensing pipeline globally. Your filters are applied before the mapping runs.' },
      { n: '3', title: 'Crisp targeted emailer', desc: 'We reach out to the specific BD owner at each shortlisted pharma with a tailored emailer telling them in 30 seconds why this asset fits THEIR pipeline.' },
      { n: '4', title: 'Full-journey partner',  desc: 'We attend the meetings, share minutes, build the data room, support negotiation, and walk with you to deal signing. You are never alone in the room.' },
    ],
    closer: 'The mapper respects your context. Already-engaged pharmas are excluded. Regional preferences are honoured. Any other constraint — competitor exclusions, sensitive geographies, must-include partners — is applied before the first outreach goes out.',
  },

  // ─── 4 · Deal Structure · Regional Licensing ───────
  {
    kind: 'regional',
    eyebrow: 'Deal Structure',
    title: 'Global or regional. We work both.',
    sub: 'Some assets command a single worldwide deal. Others unlock far more value when sliced by geography — different pharma partners can want rights to different markets, and the right carve-out can close faster and pay more in aggregate.',
    left: {
      icon: <Globe2 />,
      title: 'Global rights',
      bullets: [
        'One pharma, one term sheet, worldwide.',
        'Best when your asset has a clear lead buyer with the footprint to commercialise globally.',
        'Simpler structure — but a smaller pool of pharma can credibly compete for the mandate.',
      ],
    },
    right: {
      icon: <MapPin />,
      title: 'Regional carve-outs',
      bullets: [
        'Multiple regional buyers, separate term sheets per geography.',
        'Unlocks mid-cap and regional-leader pharma who can\u2019t compete for global rights but dominate their home market.',
        'Often pays more in aggregate, with faster individual closes and full optionality on the unsold regions.',
      ],
    },
    closer: 'We map and reach out by deal structure — not just by asset. You decide what you want to license; we find the pharma buyers who actually match that slice.',
  },

  // ─── 5 · Why us ────────────────────────────────────
  {
    kind: 'deliverables',
    eyebrow: 'Why us',
    title: 'A model designed around how biotechs actually want to work.',
    items: [
      { icon: <Lock />,        title: 'Non-exclusive',             desc: 'Run us in parallel with your in-house BD, your banker, or another agency. We don\u2019t lock you in.' },
      { icon: <DollarSign />,  title: 'No retainer · No upfront',  desc: 'Zero monthly fees. Zero engagement fees. We carry the cost of finding buyers until a deal closes.' },
      { icon: <Zap />,         title: 'Success fee only',          desc: 'Standard market success fees, paid only on a signed deal with a counter-party we introduced. No deal, no invoice.' },
      { icon: <Handshake />,   title: 'End-to-end partnership',    desc: 'We don\u2019t just send the first email. We attend meetings, share minutes, build the data room, support negotiation, and stay with you through to signing.' },
    ],
  },

  // ─── 6 · Speed & Transparency ──────────────────────
  {
    kind: 'trust',
    eyebrow: 'Speed & Transparency',
    title: 'You always know exactly where your deal stands.',
    sub: 'Traditional BD agencies sell process and ask you to trust the black box. We sell speed, full visibility, and a partner in the room. Everything in our stack — the broad-spectrum AI mapper, the live pharma pipeline index, the BD-owner database, your private deal-tracker link — exists to compress the time from "we have an asset" to "we have a signed term sheet".',
    pillars: [
      { icon: <Clock />,        title: 'Days, not months',          desc: 'AI mapping + first targeted emailer typically goes out within 5 business days of asset intake.' },
      { icon: <Network />,      title: 'Broad-spectrum AI matching', desc: 'We map across MoA, disease, therapeutic area, modality, and stage — not just mechanism. Every active pharma BD globally, scored on fit.' },
      { icon: <Mail />,         title: 'BD owner, not an alias',    desc: 'We email the specific person responsible for in-licensing in your therapeutic area — by name. Crisp tailored emailers, not blasts.' },
      { icon: <Sparkles />,     title: 'Your private deal-tracker', desc: 'A unique shareable link, just for you — live view of every pharma we approached, every reply, every meeting, every next step. Real transparency.' },
    ],
  },

  // ─── 7 · How we work together ──────────────────────
  {
    kind: 'engagement',
    eyebrow: 'How we work together',
    title: 'A handshake, not a 40-page MSA — and we stay till signing.',
    phases: [
      { phase: 'Day 0',            title: 'Intake call + non-confidential deck',  desc: '30 minutes. Share your non-confidential deck — asset, MoA, disease, indication, stage, the kind of pharma you want to be in front of.' },
      { phase: 'Day 1–3',          title: 'AI mapping + buyer shortlist',         desc: 'We return a ranked shortlist of pharmas where your asset fits an active in-licensing gap, with a named BD owner per pharma. You see your private deal-tracker link.' },
      { phase: 'Day 4–7',          title: 'Crisp emailers go live',               desc: 'Tailored emailers + introductions sent to the shortlist. You see every message before it goes out. Replies tracked live on your deal-tracker.' },
      { phase: 'Meetings → Signing', title: 'We stay in the room with you',       desc: 'We attend pharma meetings, share minutes, help build the data room, support term-sheet and negotiation, and walk you all the way to deal signing. You\u2019re never alone in the room.' },
    ],
    closer: 'No retainer. No upfront. Success fee only — payable when YOU close.',
  },

  // ─── 8 · Apply / Contact ───────────────────────────
  {
    kind: 'cta',
    eyebrow: 'Next step',
    title: 'Tell us about your asset.',
    sub: 'Send us the asset name, mechanism, indication, and current phase. We will come back within 48 hours with an initial pharma-fit read and a proposed outreach shortlist — at no cost, no commitment.',
    primary: { label: 'Send us your asset',                to: '/apply?intent=out_licensor' },
    secondary: { label: 'Email · enquiry@foresightrx.com', href: 'mailto:enquiry@foresightrx.com' },
    foot: 'ForesightRx · AI-driven biotech-to-pharma outreach. Singapore · India · Global.',
  },
];


export default function OutLicensorDeckPage() {
  return (
    <DeckShell
      slides={SLIDES}
      pdfPath="/api/public/out-licensor-deck.pdf"
      pdfFilename="ForesightRx_Out-Licensor_Deck.pdf"
      docTitle="For Biotech Out-Licensors · ForesightRx"
      testId="out-licensor-deck-page"
    />
  );
}
